import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Header from '../../layout/Header';
import styles from './AdminLayout.module.css';

interface AdminLayoutProps {
  children?: React.ReactNode;
}

interface MenuSection {
  title: string;
  items: {
    path: string;
    label: string;
    exact?: boolean;
  }[];
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // 사이드바 스크롤 위치 저장 및 복원
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 스크롤 이벤트 리스너
    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };

    // 스크롤 이벤트 등록
    container.addEventListener('scroll', handleScroll, { passive: true });

    // 이전 스크롤 위치 복원
    const restoreScrollPosition = () => {
      if (container && scrollPositionRef.current > 0) {
        container.scrollTop = scrollPositionRef.current;
      }
    };

    // DOM 업데이트 완료 후 스크롤 위치 복원
    const timeoutId = setTimeout(restoreScrollPosition, 0);
    const rafId = requestAnimationFrame(restoreScrollPosition);

    // 클린업
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname]);

  const menuSections: MenuSection[] = [
    {
      title: '대시보드',
      items: [
        { path: '/admin/dashboard', label: '대시보드 요약', exact: true }
      ]
    },
    {
      title: '홈',
      items: [
        { path: '/admin/home', label: '홈 관리' },
        { path: '/admin/home/setting', label: '기본 설정' }
      ]
    },
    {
      title: '회원',
      items: [
        { path: '/admin/user', label: '회원 관리' },
        { path: '/admin/user/qualification', label: '회원 자격 관리' }
      ]
    },
    {
      title: '클래스',
      items: [
        { path: '/admin/class', label: '클래스 관리' },
        { path: '/admin/class/lecture', label: '강의 관리' },
        { path: '/admin/class/lecture/register', label: '강의 등록' }
      ]
    },
    {
      title: '커피챗',
      items: [
        { path: '/admin/coffee-chat', label: '커피챗 관리' },
        { path: '/admin/coffee-chat/mentor', label: '멘토 관리' },
        { path: '/admin/coffee-chat/mentor-register', label: '멘토 등록' },
        { path: '/admin/coffee-chat/question', label: '커피챗 질문 관리' },
        { path: '/admin/coffee-chat/answer', label: '커피챗 답변 관리' }
      ]
    },
    {
      title: '커뮤니티',
      items: [
        { path: '/admin/community/post', label: '일반 게시글 관리' },
        { path: '/admin/community/admin-post', label: '관리자 게시글 관리' },
        { path: '/admin/community/lecture-qa', label: '강의 질문 답변 관리' },
        { path: '/admin/community/comment', label: '댓글 관리' },
        { path: '/admin/community/study', label: '스터디 관리' },
        { path: '/admin/community/write', label: '관리자 글쓰기' }
      ]
    },
    {
      title: '1대1 문의',
      items: [
        { path: '/admin/inquiry/list', label: '받은 문의' }
      ]
    }
  ];

  const handleMenuClick = (path: string, event: React.MouseEvent) => {
    // 스크롤 위치 저장
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
    
    // 네비게이션 실행
    navigate(path);
    
    // 기본 동작 방지 (혹시 모를 스크롤 리셋 방지)
    event.preventDefault();
  };

  const isItemActive = (item: any) => {
    // exact가 true인 경우 정확한 매칭
    if (item.exact) {
      return location.pathname === item.path;
    }
    // 정확히 해당 경로와 일치하는 경우만 활성화
    return location.pathname === item.path;
  };

  return (
    <>
      {/* 메인 페이지 헤더 추가 -> 임시 비활성화 */}
      {/* <Header /> */}
      
      <div className={styles.adminContainer}>
        <div className={styles.sidebar}>
          <div className={styles.logo}>
            <div className={styles.logocampuslogoHorizontal}>
              <img className={styles.tbdcamsig1Icon} alt="To Be Doctor Campus" src="/logo.svg" />
            </div>
          </div>
        
        <div className={styles.container} ref={containerRef}>
          <div className={styles.snb}>
            {menuSections.map((section) => (
              <div key={section.title} className={styles.snbadmin}>
                <div className={styles.container1}>
                  <div className={styles.heading}>
                    <div className={styles.div}>{section.title}</div>
                  </div>
                  
                  <div className={section.title === '대시보드' ? styles.content : (section.title === '1대1 문의' ? styles.content6 : styles.content1)}>
                    {section.items.map((item) => (
                      <div
                        key={item.path}
                        className={`${styles.snbresourcesmenu} ${isItemActive(item) ? styles.active : ''}`}
                        onClick={(e) => handleMenuClick(item.path, e)}
                      >
                        <div className={styles.div1}>{item.label}</div>
                        <div className={styles.interaction}>
                          <div className={`${section.title === '대시보드' ? styles.interactionnormal : styles.interactionnormal1} ${isItemActive(item) ? styles.active : ''}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.adminMain}>
        <div className={styles.mainContent}>
          {children || <Outlet />}
        </div>
      </div>
    </div>
    </>
  );
};

export default AdminLayout;
